/**
 * useWorkspaceProgress — GNB 진행 레일 상태의 단일 소유자.
 *
 * `Readiness`(재료 현황) 한 건으로 레일 축의 "채워짐"을 판정한다. 축↔재료 매핑과 순번 선언은
 * `data/analysis/workspaceRail.ts` 가 소유하고, 여기서는 그 선언을 현재 응답에 적용하기만 한다.
 *
 * ## 쿼리 키를 공유한다 — 신규 키 금지
 *
 * `['analysis','readiness']` 는 검사 화면 재료 칩(`useMaterialsStatus`)과 같은 키다. 그대로 재사용하면
 * 기존 invalidate(IpsiCardPage — 성적 저장·프로필·파기)가 **레일까지 자동 갱신**한다 — 성적을 저장하면
 * 헤더의 점이 따라 움직인다. 전용 키를 새로 만들면 이 연결이 끊겨 레일만 낡은 상태로 남는다.
 *
 * ## `staleTime` 을 지정하지 않는다
 *
 * 전역 기본값 60s(`lib/queryClient.ts`)를 그대로 쓴다. 헤더는 **모든 라우트에 있으므로**
 * `staleTime: 0` 으로 두면 라우트를 옮길 때마다 readiness 를 다시 친다(다른 화면들이 0 을 쓰는 것은
 * 그 화면에 진입할 때만 살아 있기 때문이다 — 셸에는 같은 근거가 적용되지 않는다).
 *
 * ## 비로그인은 조회하지 않는다 — `enabled` 필수
 *
 * readiness 는 인증이 필요하다. `enabled` 없이 두면 공개 라우트(`/`·`/jeongsi`·`/susi`·`/tests`,
 * 077 샘플 뷰)에서 401 이 나고 `retry: 1` 이라 2회씩 반복되며, http 인터셉터가 방문자에게 영문 모를
 * 토스트를 띄운다. 샘플 뷰가 "분석 도메인 요청 0건"을 구조로 보장하는 것과 같은 목표다.
 */
import { useQuery } from '@tanstack/vue-query';
import { computed } from 'vue';

import { fetchReadiness } from '#/api/analysis/readiness';
import {
  RAIL_AXES,
  RAIL_COPY,
  type RailAxis,
  type RailAxisState,
} from '#/data/analysis/workspaceRail';
import { useAuthStore } from '#/stores/auth';

/** 레일 한 칸의 렌더 상태 — 컴포넌트는 이 값만 읽는다. */
export interface RailAxisView extends RailAxis {
  state: RailAxisState;
  /** 상태에 맞는 보조 문구(툴팁·모바일 병기). `unknown` 이면 빈 문자열. */
  hint: string;
}

export function useWorkspaceProgress() {
  const auth = useAuthStore();

  const readinessQuery = useQuery({
    queryKey: ['analysis', 'readiness'],
    queryFn: fetchReadiness,
    enabled: computed(() => auth.isAuthenticated),
  });

  /**
   * 상태를 신뢰할 수 있는가. 비로그인·로딩·조회 실패를 한 덩어리로 묶는다.
   *
   * 로딩과 실패를 `unknown` 으로 함께 처리하는 이유: 스켈레톤 점을 깜빡이면 헤더가 매 라우트에서
   * 들썩이고, 실패 시 재시도 버튼을 헤더에 두면 본문 작업을 방해한다. 점이 없다가 조용히 나타나는
   * 편이 낫다(043 의 "오표시보다 생략" 판단 계승).
   */
  const known = computed(() => auth.isAuthenticated && readinessQuery.data.value !== undefined);

  /** 재료 key → FILLED 여부. 응답에 없는 key 는 비어 있는 것으로 본다. */
  const filledMaterials = computed(() => {
    const set = new Set<string>();
    for (const item of readinessQuery.data.value?.items ?? []) {
      if (item.status === 'FILLED') set.add(item.key);
    }
    return set;
  });

  /**
   * 축 상태 — `filled` 판정 후 "비어 있는 첫 축"만 `next` 로 올린다.
   *
   * `next` 가 정확히 하나여야 "다음에 뭘 하나"라는 질문에 답이 된다. 비어 있는 축을 전부 강조하면
   * 재개 지점이 사라진다.
   */
  const axes = computed<RailAxisView[]>(() => {
    if (!known.value) {
      return RAIL_AXES.map((axis) => ({ ...axis, state: 'unknown' as const, hint: '' }));
    }
    const filledSet = filledMaterials.value;
    let nextTaken = false;
    return RAIL_AXES.map((axis): RailAxisView => {
      const filled = axis.fills.some((key) => filledSet.has(key));
      let state: RailAxisState;
      if (filled) {
        state = 'filled';
      } else if (!nextTaken) {
        state = 'next';
        nextTaken = true;
      } else {
        state = 'empty';
      }
      const copy = RAIL_COPY[axis.key];
      return { ...axis, state, hint: filled ? copy.filledHint : copy.emptyHint };
    });
  });

  const filledCount = computed(() => axes.value.filter((a) => a.state === 'filled').length);

  return {
    axes,
    known,
    filledCount,
    totalCount: RAIL_AXES.length,
  };
}
